import moment from 'moment-timezone';

import type { Moment } from 'moment-timezone';
import type { TDate } from 'volta/models/types/calendar';
import type { TAduFrequency } from 'volta/models/types/planning-settings';
import { parseNumber } from './math-utils';

export const TIMESTAMPS_FORMATS = [
  moment.defaultFormatUtc,
  moment.defaultFormat,
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DD HH:mm:ssZ',
  'YYYY-MM-DD HH:mm:ss.SSS',
  'YYYY-MM-DD HH:mm:ss.SSSZ',
  'YYYY-MM-DD HH:mm:ss.SSSSSS',
  'YYYY-MM-DD HH:mm:ss.SSSSSSZ',
  'YYYY-MM-DD h:mm:ss A',
  'YYYY-MM-DD h:mm:ss a',
  'YYYY-MM-DD h:mm:ss A Z',
  'YYYY-MM-DD h:mm:ss a Z',
  'YYYY-MM-DDTHH:mm:ss',
  'YYYY-MM-DDTHH:mm:ssZ',
  'YYYY-MM-DDTHH:mm:ss.SSS',
  'YYYY-MM-DDTHH:mm:ss.SSSZ',
  'YYYY-MM-DDTHH:mm:ss.SSSSSS',
  'YYYY-MM-DDTHH:mm:ss.SSSSSSZ'
];

export type TPeriod = 'day' | 'week' | 'month' | 'quarter' | 'year';
export type TForecastFrequency = 'D' | 'W' | 'M' | 'Q' | 'Y';

export type TFrequencyOption = TSelectOption & {
  countLabel: string;
  dateFormat: string;
  yearFormat: string;
  period: TPeriod;
  frequency: TAduFrequency;
  label: string;
  xAxisFormat: string;
  startOf: moment.unitOfTime.StartOf;
  idFormat: string;
  forecast: TForecastFrequency;
};

export const ISO_DATE_FORMAT = 'YYYY-MM-DD' as const;
export const LOCAL_DATE_FORMAT = 'L' as const;

export const FrequencyOptions: Record<TAduFrequency, TFrequencyOption> = {
  DAILY: {
    key: 'DAILY',
    frequency: 'DAILY',
    label: 'aduFrequencies.DAILY',
    countLabel: 'daysCount',
    dateFormat: LOCAL_DATE_FORMAT,
    yearFormat: 'YYYY MMM',
    period: 'day',
    xAxisFormat: LOCAL_DATE_FORMAT,
    startOf: 'day',
    idFormat: 'YYYYMMDD',
    forecast: 'D'
  },
  WEEKLY: {
    key: 'WEEKLY',
    frequency: 'WEEKLY',
    label: 'aduFrequencies.WEEKLY',
    countLabel: 'weeksCount',
    dateFormat: 'Wo',
    yearFormat: 'YYYY [Q]Q',
    period: 'week',
    xAxisFormat: 'Wo YYYY',
    startOf: 'isoWeek',
    idFormat: 'WoYYYY',
    forecast: 'W'
  },
  MONTHLY: {
    key: 'MONTHLY',
    frequency: 'MONTHLY',
    label: 'aduFrequencies.MONTHLY',
    countLabel: 'monthsCount',
    dateFormat: 'MMM',
    yearFormat: 'YYYY',
    period: 'month',
    xAxisFormat: 'MMM YYYY',
    startOf: 'month',
    idFormat: 'MMMYYYY',
    forecast: 'M'
  },
  QUARTERLY: {
    key: 'QUARTERLY',
    frequency: 'QUARTERLY',
    label: 'aduFrequencies.QUARTERLY',
    countLabel: 'quartersCount',
    dateFormat: 'Q',
    yearFormat: 'YYYY',
    period: 'quarter',
    xAxisFormat: '[Q]Q YYYY',
    startOf: 'quarter',
    idFormat: 'QYYYY',
    forecast: 'Q'
  },
  YEARLY: {
    key: 'YEARLY',
    frequency: 'YEARLY',
    label: 'aduFrequencies.YEARLY',
    countLabel: 'yearsCount',
    dateFormat: 'YYYY',
    yearFormat: 'YYYY',
    period: 'year',
    xAxisFormat: 'YYYY',
    startOf: 'year',
    idFormat: 'YYYY',
    forecast: 'Y'
  }
} as const;

export function formatISODate(date?: moment.MomentInput) {
  return moment.utc(date).format(ISO_DATE_FORMAT);
}

export function formatLocalDate(date?: moment.MomentInput) {
  return moment.utc(date).format(LOCAL_DATE_FORMAT);
}

export function utcDateOrUndefined(date?: TDate) {
  return date ? moment.utc(date).toDate() : undefined;
}

/**
 * Get all weeks from a given month and year
 *
 * @param year Year
 * @param monthIndex Month from 0 to 11
 * @returns A list of week numbers
 */
export function getWeeksOfYearMonth(year: string | number, monthIndex: string | number) {
  // monthIndex is 0-based: 0 = January, 11 = December
  const startOfMonth = moment.utc([year, monthIndex]).startOf('month');
  const endOfMonth = moment.utc([year, monthIndex]).endOf('month');

  // We'll collect how many days each isoWeek has in this month
  const weekDayCount: Record<number, number> = {};

  // Iterate day by day over the month
  const current = startOfMonth.clone();
  while (current.isSameOrBefore(endOfMonth, 'day')) {
    const isoWeekNum = current.isoWeek();

    // Initialize count for this isoWeek if not present
    if (!weekDayCount[isoWeekNum]) {
      weekDayCount[isoWeekNum] = 0;
    }

    // Increment the count for this day's isoWeek
    weekDayCount[isoWeekNum]++;

    // Move to the next day
    current.add(1, 'day');
  }

  // Filter weeks that have at least 4 days in this month
  return Object.entries(weekDayCount)
    .filter(([_, count]) => count >= 4)
    .map(([week]) => parseInt(week, 10))
    .sort((a, b) => a - b);
}

/**
 * Returns an array of JavaScript Date objects. Each Date is the
 * Monday (start of ISO week) of every ISO week in the given year.
 *
 * @param year - The target ISO year (e.g. "2024" or 2024)
 * @returns Date[] - An array of Mondays for each ISO week of that year
 */
export function getEveryYearWeek(year: string | number): Date[] {
  // Convert year to number if it's a string
  const numericYear = parseNumber(year);

  if (numericYear === undefined) {
    return [];
  }

  // According to ISO rules:
  // - The first ISO week is the one that contains January 4th.
  // - .startOf("isoWeek") will jump us to Monday of that week.
  const firstIsoWeekOfYear = moment
    .utc([numericYear, 0, 4]) // January 4th
    .startOf('isoWeek')
    .isoWeekYear(numericYear); // Ensure we’re pinned to the correct ISO year

  const weeksStartDates: Date[] = [];

  // Clone so we don't mutate our original moment
  let current = firstIsoWeekOfYear.clone();

  // ISO years have either 52 or 53 weeks; we continue until we roll into the next ISO year
  while (current.isoWeekYear() === numericYear) {
    // Push the current Monday (start of ISO week) as a JS Date
    weeksStartDates.push(current.toDate());

    // Move to the next week
    current.add(1, 'week');
  }

  return weeksStartDates;
}

export function formatDuration(hours: number, dayFormat?: (days: number) => string) {
  let result = '';
  const daysDecimal = hours / 24;
  const days = Math.floor(daysDecimal);
  const remainingHours = days > 0 ? (daysDecimal - days) * 24 : hours;
  if (days > 0) {
    result += (dayFormat?.(days) ?? `${days} d`) + (remainingHours === 0 ? '' : ' ');
  }

  if (remainingHours > 0) {
    const date = new Date(0);
    date.setSeconds(remainingHours * 3600); // specify value for SECONDS here
    result += date.toISOString().substring(11, 19);
  }
  return result;
}

export function formatDurationInHours(hours?: number) {
  if (hours == null || isNaN(hours)) {
    return undefined;
  }

  if (Number.isInteger(hours)) {
    return `${hours}h`;
  }

  if (hours < 1) {
    const totalMinutes = hours * 60;
    const minutes = Math.floor(totalMinutes);
    const seconds = Math.round((totalMinutes - minutes) * 60);
    if (seconds === 0) {
      return `${minutes}m`;
    } else {
      return `${minutes}m ${seconds}s`;
    }
  }

  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);
  if (minutes === 0) {
    return `${wholeHours}h`;
  }
  return `${wholeHours}h ${minutes}m`;
}

export function getDuration(
  start: Date | Moment | undefined,
  end: Date | Moment | undefined,
  symbolNull?: string | number
) {
  if (!start || !end) {
    return symbolNull ? `${symbolNull}` : '';
  }

  const ms = moment.utc(end).diff(moment.utc(start));
  const timeImport = moment.duration(ms);

  if (ms < 1000) {
    return '< 1s.';
  }
  if (ms >= 86_400_000) {
    return '> 24h.';
  }

  const hours = timeImport.hours() < 10 ? '0' + timeImport.hours() : timeImport.hours();
  const mn = timeImport.minutes() < 10 ? '0' + timeImport.minutes() : timeImport.minutes();
  const sec = timeImport.seconds() < 10 ? '0' + timeImport.seconds() : timeImport.seconds();

  return hours + ':' + mn + ':' + sec;
}

export function getGreetingTime(date: Date | string | Moment = moment.utc()) {
  const m = moment.utc(date);
  let g = null; // return g

  if (!m || !m.isValid()) {
    return;
  } // if we can't find a valid or filled moment, we return.

  const splitAfternoon = 12; // 24hr time to split the afternoon
  const splitEvening = 17; // 24hr time to split the evening
  const currentHour = Number.parseFloat(m.format('HH'));

  if (currentHour >= splitAfternoon && currentHour <= splitEvening) {
    g = 'afternoon';
  } else if (currentHour >= splitEvening) {
    g = 'evening';
  } else {
    g = 'morning';
  }

  return g;
}

/**
 * Checks if a string value is a valid JS date
 * @param date String date value to check
 * @param formats The strict formats to check, by default it will check 'YYYY-MM-DD'
 * @returns true if valid
 */
export function isDate(
  date: moment.MomentInput,
  formats: string[] = [ISO_DATE_FORMAT, LOCAL_DATE_FORMAT]
): boolean {
  return moment.utc(date, formats, true).isValid();
}

/**
 * This function validates a date by parsing usually a string value and trying to match on given formats
 * When the value cannot be parsed, undefined is returned, otherwise the Date value.
 *
 * @param value Date value to parse, it can be any type readable by Moment (string, number, Date)
 * @param formats Formats hints to try given to the Moment parsing function
 * @returns The value as a Date object if valid, undefined otherwise.
 */
export const validDateOrUndefined = (
  value?: moment.MomentInput,
  formats: string[] = [ISO_DATE_FORMAT, LOCAL_DATE_FORMAT]
): Moment | undefined => {
  const m = moment.utc(value, formats, true);
  return value && m.isValid() ? m : undefined;
};

/**
 * This function evaluates whether a given date can be considered a weekend day.
 * The list of week-end days must be supplied otherwise it will always return false as no week-end days is considered.
 *
 * @param date Value to be parsed as date
 * @param weekendDays 1-indexed array of weekday number that are considered week-end days (eg. [6,7] for Saturday and Sunday)
 * @returns True if the date is a week-end, false otherwise
 */
export const isWeekendDay = (date: TDate, weekendDays?: number[]): boolean => {
  return (weekendDays && weekendDays.includes(moment.utc(date).isoWeekday())) ?? false;
};

/**
 * From: https://github.com/moment/moment/issues/3341
 * This function format a localized format with days and month.
 * Also, it removes the year in the formatting if it's current.
 *
 * @param date Value to format
 * @returns A localized format with days, months, and year when not current
 * @example '23/03'
 * @example '02/12/2024'
 */
export const localizedFormatMD = (date: moment.Moment) =>
  formatLocalDate(date).replace(new RegExp('[^.]?' + date.format('YYYY') + '.?'), '');

/**
 * This function format the date for display purposes based on the due date of the element
 * ex: if the element is 1 day in the future it will display : 'tomorrow'
 * @param dueDate due date of the element
 * @param today the translation of today in the user language
 * @param tomorrow the translation of tomorrow in the user language
 */
export const formatRelativeDate = (dueDate: Date | undefined, today: string, tomorrow: string) => {
  let formattedDate = undefined;
  if (dueDate) {
    const mDueDate = moment.utc(dueDate);
    const mToday = moment.utc();

    if (!mDueDate.isSame(mToday, 'year')) {
      formattedDate = mDueDate.format('MM/YYYY');
    } else if (mDueDate.isSame(mToday, 'day')) {
      formattedDate = today;
    } else if (mDueDate.isSame(mToday.clone().add(1, 'day'), 'day')) {
      formattedDate = tomorrow;
    } else if (
      mDueDate.dayOfYear() > mToday.dayOfYear() &&
      mDueDate.dayOfYear() < mToday.dayOfYear() + 15
    ) {
      // We can not use 'fromNow' as it will add one more day diff because of the hour difference
      formattedDate = mDueDate.from(mToday.clone().startOf('day'));
    } else {
      formattedDate = localizedFormatMD(mDueDate);
    }
  }
  return formattedDate;
};
